const BOARD_CACHE_KEY = 'provio-admin-board-cache'

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null)

export const safeJsonParse = (value, fallback = null) => {
  if (value === null || value === undefined || value === '') return fallback
  if (typeof value !== 'string') return value

  try {
    return JSON.parse(value)
  } catch {
    return fallback
  }
}

const toArray = (value) => {
  const parsed = safeJsonParse(value, value)
  if (Array.isArray(parsed)) return parsed
  return parsed ? [parsed] : []
}

const normalizeString = (value) => String(value ?? '').trim()

const normalizeOptionArray = (value) =>
  toArray(value)
    .flatMap((item) => {
      if (item === null || item === undefined || item === '') return []

      if (typeof item === 'string' || typeof item === 'number') {
        const label = normalizeString(item)
        return label ? [{ value: label, label }] : []
      }

      if (typeof item === 'object') {
        const label = normalizeString(
          firstDefined(
            item.label,
            item.Username,
            item.username,
            item.name,
            item.Name,
            item.value,
            item.UserId,
            item.userId,
            item.Id,
            item.id,
          ),
        )

        return label
          ? [
              {
                ...item,
                value: firstDefined(item.value, item.ref_id, item.refId, item.RefId, item.RefID, item.UserId, item.userId, item.Id, item.id, label),
                label,
              },
            ]
          : []
      }

      const label = normalizeString(item)
      return label ? [{ value: label, label }] : []
    })

export const parseCrudPayload = (response) => {
  if (!response) return null

  const rawPayload =
    response?.d !== undefined
      ? response.d
      : response?.success !== undefined || response?.error !== undefined || response?.message !== undefined || response?.Data !== undefined
        ? response
        : response?.data !== undefined && Object.keys(response || {}).length === 1
          ? response.data
          : response

  return safeJsonParse(rawPayload, rawPayload)
}

export const getCrudData = (response) => {
  const payload = parseCrudPayload(response)
  const data = payload?.data ?? payload?.Data ?? payload

  if (Array.isArray(data)) return data
  return data ? [data] : []
}

export const getCrudRecords = (response, resultIndex = 0) => {
  const data = getCrudData(response)
  const selectedData = Array.isArray(data?.[resultIndex]) ? data[resultIndex] : data

  if (Array.isArray(selectedData)) return selectedData
  return selectedData ? [selectedData] : []
}

const normalizeColumnRecord = (column, index = 0) => {
  if (!column) return null

  const headerText = normalizeString(
    firstDefined(
      column.headerText,
      column.HeaderText,
      column.name,
      column.Name,
      column.StatusName,
      column.Status,
      column.status,
      column.title,
      column.Title,
    ),
  )

  const keyField = firstDefined(
    column.keyField,
    column.KeyField,
    column.StatusId,
    column.StatusID,
    column.statusId,
    column.id,
    column.Id,
    column.ID,
    headerText || index + 1,
  )

  if (!headerText && keyField === null) return null

  return {
    ...column,
    keyField,
    headerText: headerText || normalizeString(keyField),
    order: firstDefined(column.order, column.Order, column.order_index, column.OrderIndex, index + 1),
    wipLimit: firstDefined(column.wipLimit, column.WipLimit, column.wip_limit, column.WIPLimit, 0),
  }
}

export const normalizeBoardColumns = (columns = []) =>
  toArray(columns)
    .map(normalizeColumnRecord)
    .filter(Boolean)
    .sort((a, b) => Number(a.order || 0) - Number(b.order || 0))

export const normalizeBoardRecord = (board = {}) => {
  const boardData = safeJsonParse(firstDefined(board.Data, board.data, board.BoardData, board.boardData), {}) || {}
  const id = firstDefined(
    board.id,
    board.Id,
    board.ID,
    board.BoardId,
    board.BoardID,
    board.boardId,
    boardData.id,
    boardData.Id,
    boardData.BoardId,
  )
  const title = normalizeString(
    firstDefined(
      board.title,
      board.Title,
      board.name,
      board.Name,
      board.BoardName,
      boardData.title,
      boardData.Title,
      boardData.name,
      boardData.Name,
    ),
  )

  if (!id && !title) return null

  return {
    ...board,
    ...boardData,
    id,
    boardId: id,
    title: title || 'Untitled board',
    name: title || 'Untitled board',
    description: firstDefined(
      board.description,
      board.Description,
      boardData.description,
      boardData.Description,
      '',
    ),
    columns: normalizeBoardColumns(
      firstDefined(board.columns, board.Columns, boardData.columns, boardData.Columns, []),
    ),
    taskWatchers: normalizeOptionArray(
      firstDefined(board.taskWatchers, board.TaskWatchers, board.members, board.Members, boardData.members, []),
    ),
  }
}

export const extractBoardsFromCrudResponse = (response) => {
  const payload = parseCrudPayload(response)
  const data = payload?.data ?? payload?.Data ?? []

  if (!Array.isArray(data)) {
    const board = normalizeBoardRecord(data)
    return board ? [board] : []
  }

  if (Array.isArray(data[0])) {
    const boardRows = data[0] || []
    const statusRows = Array.isArray(data[1]) ? data[1] : []

    return boardRows
      .map((board) => normalizeBoardRecord({ ...board, columns: statusRows }))
      .filter(Boolean)
  }

  return data
    .map((board) => normalizeBoardRecord(board))
    .filter(Boolean)
}

const storageAvailable = () =>
  typeof window !== 'undefined' && Boolean(window.localStorage)

// export const readCachedBoards = () => {
//   if (!storageAvailable()) return []

//   return toArray(safeJsonParse(window.localStorage.getItem(BOARD_CACHE_KEY), []))
//     .map((board) => normalizeBoardRecord(board))
//     .filter(Boolean)
// }

// export const writeCachedBoards = (boards = []) => {
//   if (!storageAvailable()) return []

//   const normalizedBoards = boards
//     .map((board) => normalizeBoardRecord(board))
//     .filter(Boolean)

//   window.localStorage.setItem(BOARD_CACHE_KEY, JSON.stringify(normalizedBoards))
//   return normalizedBoards
// }

// export const mergeCachedBoards = (boards = []) => {
//   const merged = new Map()

//   readCachedBoards().forEach((board) => {
//     if (board?.id !== undefined && board?.id !== null) merged.set(String(board.id), board)
//   })

//   boards
//     .map((board) => normalizeBoardRecord(board))
//     .filter(Boolean)
//     .forEach((board) => {
//       merged.set(String(board.id), board)
//     })

//   return writeCachedBoards(Array.from(merged.values()))
// }

// export const removeCachedBoard = (boardId) => {
//   const normalizedId = String(boardId ?? '')
//   if (!normalizedId) return readCachedBoards()

//   return writeCachedBoards(
//     readCachedBoards().filter((board) => String(board.id) !== normalizedId),
//   )
// }

// export const findCachedBoard = (boardId) => {
//   const normalizedId = String(boardId ?? '')
//   if (!normalizedId) return null

//   return readCachedBoards().find((board) => String(board.id) === normalizedId) || null
// }
